// ========================================
// ABOUT MANAGER
// Gestionnaire de la page À Propos
// ========================================

import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/admin/Layout/AdminLayout';
import useAboutPage from '../../../hooks/useAboutPage';
import { ImageUploader, RichTextEditor } from '../../../components/admin/Editor';
import { uploadDocumentToCloudinary } from '../../../services/cloudinary/config';
import {
  FiSave,
  FiUser,
  FiFileText,
  FiHeart,
  FiClock,
  FiAward,
  FiMail,
  FiLoader,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiCheck,
  FiUpload,
  FiDownload,
  FiX
} from 'react-icons/fi';
import './AboutManager.css';

// Icônes disponibles pour les valeurs
const AVAILABLE_ICONS = [
  { value: 'heart', label: '❤️ Cœur' },
  { value: 'users', label: '👥 Utilisateurs' },
  { value: 'lightbulb', label: '💡 Idée' },
  { value: 'target', label: '🎯 Cible' },
  { value: 'star', label: '⭐ Étoile' },
  { value: 'shield', label: '🛡️ Bouclier' },
  { value: 'globe', label: '🌍 Globe' },
  { value: 'handshake', label: '🤝 Partenariat' }
];

const AboutManager = () => {
  const { 
    content, 
    loading, 
    saving, 
    saveProfile, 
    saveBio, 
    saveValues, 
    saveTimeline, 
    saveSkills, 
    saveContact,
    saveCV 
  } = useAboutPage();

  const [activeTab, setActiveTab] = useState('profile');

  // États locaux
  const [profileData, setProfileData] = useState(null);
  const [bioData, setBioData] = useState('');
  const [valuesData, setValuesData] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [skillsData, setSkillsData] = useState([]);
  const [contactData, setContactData] = useState(null);
  const [cvUrl, setCvUrl] = useState(null);

  // États d'édition
  const [editingValue, setEditingValue] = useState(null);
  const [editingTimeline, setEditingTimeline] = useState(null);
  const [editingSkill, setEditingSkill] = useState(null);
  const [uploadingCV, setUploadingCV] = useState(false);

  // Initialiser les données
  useEffect(() => {
    if (content) {
      setProfileData(content.profile);
      setBioData(content.bio || '');
      setValuesData(content.values || []);
      setTimelineData(content.timeline || []);
      setSkillsData(content.skills || []);
      setContactData(content.contact);
      setCvUrl(content.cvUrl);
    }
  }, [content]);

  const tabs = [
    { id: 'profile', label: 'Profil', icon: FiUser },
    { id: 'bio', label: 'Biographie', icon: FiFileText },
    { id: 'values', label: 'Valeurs', icon: FiHeart },
    { id: 'timeline', label: 'Parcours', icon: FiClock },
    { id: 'skills', label: 'Compétences', icon: FiAward },
    { id: 'contact', label: 'Contact & CV', icon: FiMail }
  ];

  // Handlers de sauvegarde
  const handleSaveProfile = () => saveProfile(profileData);
  const handleSaveBio = () => saveBio(bioData);
  const handleSaveValues = () => saveValues(valuesData);
  const handleSaveTimeline = () => saveTimeline(timelineData);
  const handleSaveSkills = () => saveSkills(skillsData);
  const handleSaveContact = () => {
    saveContact(contactData);
    if (cvUrl !== content?.cvUrl) {
      saveCV(cvUrl);
    }
  };

  // Gestion des valeurs
  const addValue = () => {
    const newValue = {
      id: Date.now().toString(),
      icon: 'heart',
      title: 'Nouvelle valeur',
      description: 'Description de la valeur',
      order: valuesData.length
    };
    setValuesData([...valuesData, newValue]);
    setEditingValue(newValue.id);
  };

  const updateValue = (id, field, value) => {
    setValuesData(prev => prev.map(v => v.id === id ? { ...v, [field]: value } : v));
  };

  const deleteValue = (id) => {
    setValuesData(prev => prev.filter(v => v.id !== id));
  };

  // Gestion du parcours
  const addTimelineItem = () => {
    const newItem = {
      id: Date.now().toString(),
      year: new Date().getFullYear().toString(),
      organization: 'Nouvelle organisation',
      role: 'Poste occupé',
      description: 'Description des missions',
      order: timelineData.length
    };
    setTimelineData([...timelineData, newItem]);
    setEditingTimeline(newItem.id);
  };

  const updateTimelineItem = (id, field, value) => {
    setTimelineData(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const deleteTimelineItem = (id) => {
    setTimelineData(prev => prev.filter(t => t.id !== id));
  };

  // Gestion des compétences
  const addSkill = () => {
    const newSkill = {
      id: Date.now().toString(),
      name: 'Nouvelle compétence',
      percentage: 75
    };
    setSkillsData([...skillsData, newSkill]);
    setEditingSkill(newSkill.id);
  };

  const updateSkill = (id, field, value) => {
    setSkillsData(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const deleteSkill = (id) => {
    setSkillsData(prev => prev.filter(s => s.id !== id));
  };

  // Upload CV
  const handleCVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Veuillez sélectionner un fichier PDF');
      return;
    }

    setUploadingCV(true);
    try {
      const result = await uploadDocumentToCloudinary(file, 'portfolio/cv');
      setCvUrl(result.url);
    } catch (error) {
      console.error('Erreur upload CV:', error);
    } finally {
      setUploadingCV(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="manager-loading">
          <FiLoader className="spinner" />
          <p>Chargement...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="about-manager">
        {/* Header */}
        <div className="manager-header">
          <div>
            <h1>Page À Propos</h1>
            <p>Personnalisez votre profil et vos informations</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="manager-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`manager-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="manager-content">
          
          {/* PROFIL */}
          {activeTab === 'profile' && profileData && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Informations du profil</h2>
                <p>Votre photo et votre titre professionnel</p>
              </div>

              <div className="form-card">
                <div className="profile-form-grid">
                  <div className="profile-image-section">
                    <label className="form-label">Photo de profil</label>
                    <ImageUploader
                      value={profileData.image}
                      onChange={(url) => setProfileData({ ...profileData, image: url })}
                      folder="portfolio/profile"
                      placeholder="Votre photo"
                    />
                  </div>

                  <div className="profile-info-section">
                    <div className="form-group">
                      <label className="form-label">Nom complet</label>
                      <input
                        type="text"
                        className="form-input"
                        value={profileData.name || ''}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        placeholder="Gustave KONATE"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Titre / Fonction</label>
                      <input
                        type="text"
                        className="form-input"
                        value={profileData.title || ''}
                        onChange={(e) => setProfileData({ ...profileData, title: e.target.value })}
                        placeholder="Communicant • Écrivain • Leader Associatif"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn btn-primary" onClick={handleSaveProfile} disabled={saving}>
                    {saving ? <FiLoader className="spinner" /> : <FiSave />}
                    Sauvegarder
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* BIOGRAPHIE */}
          {activeTab === 'bio' && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Votre histoire</h2>
                <p>Racontez votre parcours et vos motivations</p>
              </div>

              <div className="form-card">
                <RichTextEditor
                  value={bioData}
                  onChange={setBioData}
                  placeholder="Écrivez votre biographie..."
                  minHeight={350}
                />

                <div className="form-actions">
                  <button className="btn btn-primary" onClick={handleSaveBio} disabled={saving}>
                    {saving ? <FiLoader className="spinner" /> : <FiSave />}
                    Sauvegarder
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* VALEURS */}
          {activeTab === 'values' && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Vos valeurs</h2>
                <p>Les principes qui guident votre travail</p>
              </div>

              <div className="items-list">
                {valuesData.map((value) => (
                  <div key={value.id} className="item-card">
                    {editingValue === value.id ? (
                      <div className="item-edit-form">
                        <div className="form-row three-cols">
                          <div className="form-group">
                            <label className="form-label">Icône</label>
                            <select
                              className="form-input"
                              value={value.icon}
                              onChange={(e) => updateValue(value.id, 'icon', e.target.value)}
                            >
                              {AVAILABLE_ICONS.map(icon => (
                                <option key={icon.value} value={icon.value}>{icon.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="form-label">Titre</label>
                            <input
                              type="text"
                              className="form-input"
                              value={value.title}
                              onChange={(e) => updateValue(value.id, 'title', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Description</label>
                          <input
                            type="text"
                            className="form-input"
                            value={value.description}
                            onChange={(e) => updateValue(value.id, 'description', e.target.value)}
                          />
                        </div>
                        <div className="item-edit-actions">
                          <button className="btn btn-sm btn-primary" onClick={() => setEditingValue(null)}>
                            <FiCheck /> Terminé
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="item-preview">
                        <div className="item-icon">
                          {AVAILABLE_ICONS.find(i => i.value === value.icon)?.label.split(' ')[0] || '❤️'}
                        </div>
                        <div className="item-info">
                          <h4>{value.title}</h4>
                          <p>{value.description}</p>
                        </div>
                        <div className="item-actions">
                          <button className="action-btn" onClick={() => setEditingValue(value.id)}>
                            <FiEdit2 />
                          </button>
                          <button className="action-btn danger" onClick={() => deleteValue(value.id)}>
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <button className="add-item-btn" onClick={addValue}>
                  <FiPlus /> Ajouter une valeur
                </button>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleSaveValues} disabled={saving}>
                  {saving ? <FiLoader className="spinner" /> : <FiSave />}
                  Sauvegarder les valeurs
                </button>
              </div>
            </div>
          )}

          {/* PARCOURS */}
          {activeTab === 'timeline' && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Votre parcours</h2>
                <p>Vos expériences professionnelles et formations</p>
              </div>

              <div className="items-list timeline-list">
                {timelineData.sort((a, b) => b.year - a.year).map((item) => (
                  <div key={item.id} className="item-card timeline-card">
                    {editingTimeline === item.id ? (
                      <div className="item-edit-form">
                        <div className="form-row two-cols">
                          <div className="form-group">
                            <label className="form-label">Année</label>
                            <input
                              type="text"
                              className="form-input"
                              value={item.year}
                              onChange={(e) => updateTimelineItem(item.id, 'year', e.target.value)}
                              placeholder="2024"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Poste / Rôle</label>
                            <input
                              type="text"
                              className="form-input"
                              value={item.role}
                              onChange={(e) => updateTimelineItem(item.id, 'role', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Organisation</label>
                          <input
                            type="text"
                            className="form-input"
                            value={item.organization}
                            onChange={(e) => updateTimelineItem(item.id, 'organization', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-input form-textarea"
                            value={item.description}
                            onChange={(e) => updateTimelineItem(item.id, 'description', e.target.value)}
                            rows={2}
                          />
                        </div>
                        <div className="item-edit-actions">
                          <button className="btn btn-sm btn-primary" onClick={() => setEditingTimeline(null)}>
                            <FiCheck /> Terminé
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="item-preview timeline-preview">
                        <div className="timeline-year">{item.year}</div>
                        <div className="timeline-content">
                          <h4>{item.organization}</h4>
                          <span className="timeline-role">{item.role}</span>
                          <p>{item.description}</p>
                        </div>
                        <div className="item-actions">
                          <button className="action-btn" onClick={() => setEditingTimeline(item.id)}>
                            <FiEdit2 />
                          </button>
                          <button className="action-btn danger" onClick={() => deleteTimelineItem(item.id)}>
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <button className="add-item-btn" onClick={addTimelineItem}>
                  <FiPlus /> Ajouter une expérience
                </button>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleSaveTimeline} disabled={saving}>
                  {saving ? <FiLoader className="spinner" /> : <FiSave />}
                  Sauvegarder le parcours
                </button>
              </div>
            </div>
          )}

          {/* COMPÉTENCES */}
          {activeTab === 'skills' && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Vos compétences</h2>
                <p>Mettez en avant vos domaines d'expertise</p>
              </div>

              <div className="skills-list">
                {skillsData.map((skill) => (
                  <div key={skill.id} className="skill-card">
                    {editingSkill === skill.id ? (
                      <div className="skill-edit-form">
                        <div className="form-group">
                          <input
                            type="text"
                            className="form-input"
                            value={skill.name}
                            onChange={(e) => updateSkill(skill.id, 'name', e.target.value)}
                            placeholder="Nom de la compétence"
                          />
                        </div>
                        <div className="form-group">
                          <div className="range-input-group">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={skill.percentage}
                              onChange={(e) => updateSkill(skill.id, 'percentage', parseInt(e.target.value))}
                            />
                            <span className="range-value">{skill.percentage}%</span>
                          </div>
                        </div>
                        <button className="btn btn-sm btn-primary" onClick={() => setEditingSkill(null)}>
                          <FiCheck />
                        </button>
                      </div>
                    ) : (
                      <div className="skill-preview">
                        <div className="skill-info">
                          <span className="skill-name">{skill.name}</span>
                          <span className="skill-percentage">{skill.percentage}%</span>
                        </div>
                        <div className="skill-bar">
                          <div className="skill-progress" style={{ width: `${skill.percentage}%` }}></div>
                        </div>
                        <div className="skill-actions">
                          <button className="action-btn" onClick={() => setEditingSkill(skill.id)}>
                            <FiEdit2 />
                          </button>
                          <button className="action-btn danger" onClick={() => deleteSkill(skill.id)}>
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <button className="add-item-btn" onClick={addSkill}>
                  <FiPlus /> Ajouter une compétence
                </button>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleSaveSkills} disabled={saving}>
                  {saving ? <FiLoader className="spinner" /> : <FiSave />}
                  Sauvegarder les compétences
                </button>
              </div>
            </div>
          )}

          {/* CONTACT & CV */}
          {activeTab === 'contact' && contactData && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Contact & CV</h2>
                <p>Vos coordonnées et votre CV téléchargeable</p>
              </div>

              <div className="contact-grid">
                {/* Informations de contact */}
                <div className="form-card">
                  <h3><FiMail /> Informations de contact</h3>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={contactData.email || ''}
                      onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Téléphone(s)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={contactData.phones?.join(', ') || ''}
                      onChange={(e) => setContactData({ 
                        ...contactData, 
                        phones: e.target.value.split(',').map(p => p.trim()) 
                      })}
                      placeholder="+226 70 11 64 34, +226 76 96 57 05"
                    />
                    <span className="form-hint">Séparez les numéros par une virgule</span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Localisation</label>
                    <input
                      type="text"
                      className="form-input"
                      value={contactData.location || ''}
                      onChange={(e) => setContactData({ ...contactData, location: e.target.value })}
                      placeholder="Ouagadougou, Burkina Faso"
                    />
                  </div>
                </div>

                {/* CV */}
                <div className="form-card">
                  <h3><FiFileText /> Curriculum Vitae</h3>

                  <div className="cv-upload-area">
                    {cvUrl ? (
                      <div className="cv-preview">
                        <div className="cv-icon">📄</div>
                        <div className="cv-info">
                          <span>CV téléchargé</span>
                          <a href={cvUrl} target="_blank" rel="noopener noreferrer">
                            <FiDownload /> Voir le fichier
                          </a>
                        </div>
                        <button 
                          className="remove-cv-btn"
                          onClick={() => setCvUrl(null)}
                        >
                          <FiX />
                        </button>
                      </div>
                    ) : (
                      <label className="cv-upload-zone">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handleCVUpload}
                          disabled={uploadingCV}
                          style={{ display: 'none' }}
                        />
                        {uploadingCV ? (
                          <>
                            <FiLoader className="spinner" />
                            <span>Upload en cours...</span>
                          </>
                        ) : (
                          <>
                            <FiUpload />
                            <span>Cliquez pour uploader votre CV</span>
                            <small>Format PDF uniquement</small>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleSaveContact} disabled={saving}>
                  {saving ? <FiLoader className="spinner" /> : <FiSave />}
                  Sauvegarder
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </AdminLayout>
  );
};

export default AboutManager;